import { useState } from "react";
import { BUYER_SEQUENCE_SIGNATURE_KEY } from "@/api/buyerSequence";
import { extractErrorMessage } from "@/api/client";
import { Button, ResponsiveGrid } from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import { TemplateChips } from "./TemplateChips";
import { useDeletePersonal, useWording, useWordingFields } from "./useWording";

/**
 * L11's notice: "The team default for this step changed on {date}, after you
 * made your version. Yours is still the one used." with Compare and USE TEAM
 * DEFAULT. Send mode renders it when the selected step's wording reports
 * `teamChangedSincePersonal`; Edit wording renders it above "My version".
 *
 * It fetches the wording itself rather than trusting the caller, so it renders
 * nothing — not an empty box — for anyone whose version is still current.
 */
export interface TeamChangedNoticeProps {
  buyerId: string;
  templateKey: string;
}

export function TeamChangedNotice({ templateKey }: TeamChangedNoticeProps) {
  const q = useWording(templateKey);
  const fields = useWordingFields();
  const deletePersonal = useDeletePersonal(templateKey);
  const { date } = useDateFormatter();
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wording = q.data;
  if (!wording || !wording.teamChangedSincePersonal) return null;

  const subject =
    templateKey === BUYER_SEQUENCE_SIGNATURE_KEY ? "your signature" : "this step";
  // teamChangedAtUtc is an instant — the moment somebody published — so it is
  // read as a calendar date in the viewer's own zone.
  const when = wording.teamChangedAtUtc ? date(wording.teamChangedAtUtc) : "a later date";

  return (
    <div
      role="status"
      className="flex flex-col gap-space-2 rounded-md border border-status-warning-ink bg-status-warning/10 p-space-3"
      data-testid="team-changed-notice"
    >
      <p className="text-small text-onyx">
        The team default for {subject} changed on {when}, after you made your version.
        Yours is still the one used.
      </p>
      <div className="flex flex-wrap items-center gap-space-2">
        <Button
          size="sm"
          variant="secondary"
          aria-expanded={comparing}
          onClick={() => setComparing((c) => !c)}
        >
          {comparing ? "Hide comparison" : "Compare"}
        </Button>
        <Button
          size="sm"
          loading={deletePersonal.isPending}
          onClick={() => {
            setError(null);
            deletePersonal.mutate(undefined, {
              onError: (e) => setError(extractErrorMessage(e)),
            });
          }}
        >
          Use team default
        </Button>
      </div>

      {error && (
        <p role="alert" className="text-caption text-status-error">
          {error}
        </p>
      )}

      {comparing && (
        <div className="flex flex-col gap-space-3" data-testid="team-changed-compare">
          {wording.blocks.map((b) => (
            <div key={b.blockKey} className="flex flex-col gap-space-1">
              <span className="text-caption font-semibold uppercase tracking-wide text-gray-mid">
                {b.label}
              </span>
              <ResponsiveGrid cols={2} minColWidth="16rem" gap="sm">
                <div
                  className="flex min-w-0 flex-col gap-space-1 rounded-md border border-border-light bg-white-pure p-space-2"
                  data-testid={`team-changed-mine-${b.blockKey}`}
                >
                  <span className="text-caption font-semibold text-onyx">Your version</span>
                  <TemplateChips text={b.personalText ?? b.teamText} fields={fields.data} />
                </div>
                <div
                  className="flex min-w-0 flex-col gap-space-1 rounded-md border border-border-light bg-white-pure p-space-2"
                  data-testid={`team-changed-team-${b.blockKey}`}
                >
                  <span className="text-caption font-semibold text-onyx">Team default</span>
                  <TemplateChips text={b.teamText} fields={fields.data} />
                </div>
              </ResponsiveGrid>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
